import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeftIcon, ChevronRightIcon, PhoneIcon, GiftIcon } from '@heroicons/react/24/outline';
import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.webp';
import banner3 from '../assets/banner3.jpg';
import whatsappIcon from '../assets/whatsapp-icon.png';

// Configuración centralizada
const CONFIG = {
  AUTO_ADVANCE_INTERVAL: 7000,
  PAUSE_DURATION: 10000,
  LOADING_DELAY: 1000,
  ANIMATION_DURATION: 500,
  WHATSAPP_NUMBER: process.env.REACT_APP_WHATSAPP_NUMBER || '3325456522',
  PHONE_NUMBER: process.env.REACT_APP_PHONE_NUMBER || '3325456522'
};

// Datos de banners con configuración mejorada
const DEFAULT_BANNERS = [
  {
    id: 'especializado',
    image: banner1, 
    alt: "Enfermera certificada brindando atención geriátrica de élite a adulto mayor",
    title: 'Cuidado geriátrico de élite para su familiar en Guadalajara',
    buttonText: 'Solicitar atención',
    actionType: 'call'
  },
  { 
    id: 'disponibilidad',
    image: banner2, 
    alt: "Enfermera profesional con estándares hospitalarios premium brindando servicio exclusivo",
    title: 'Recuperación postoperatoria con estándares hospitalarios en su hogar',
    subtitle: 'Supervisión médica continua y plan de recuperación personalizado',
    buttonText: 'Consultar ahora',
    actionType: 'whatsapp'
  },
  { 
    id: 'integral',
    image: banner3, 
    alt: "Equipo médico certificado con atención geriátrica integral de primer nivel",
    title: 'Aplicación de medicamentos intravenosos e inyecciones a domicilio',
    buttonText: 'Solicitar servicio',
    actionType: 'quote'
  }
];

// Hook personalizado para el timer del carousel
const useCarouselTimer = (isActive, interval, callback) => {
  const timeoutRef = useRef(null);
  
  useEffect(() => {
    if (isActive) {
      timeoutRef.current = setInterval(callback, interval);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    };
  }, [isActive, interval, callback]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return clearTimer;
};

// Hook para detectar si el usuario prefiere movimiento reducido
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (event) => setPrefersReducedMotion(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  return prefersReducedMotion;
};

// Componente de carga optimizado
const LoadingSpinner = () => (
  <section 
    className="relative h-[520px] sm:h-[600px] md:h-[680px] overflow-hidden bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center"
    aria-label="Cargando contenido del banner"
  >
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      <p className="text-white text-lg font-medium">Cargando servicios...</p>
    </div>
  </section>
);

// Componente de imagen optimizado para LCP
const OptimizedImage = React.memo(({ banner, isActive, isFirstSlide, onError }) => (
  <div className="absolute inset-0">
    <img
      src={banner.image}
      alt={banner.alt}
      className={`w-full h-full object-cover transition-opacity duration-500 ${
        isActive ? 'opacity-100' : 'opacity-0'
      }`}
      // Primera imagen (LCP): carga prioritaria, sin lazy
      // Otras imágenes: lazy loading para mejor rendimiento
      loading={isFirstSlide ? 'eager' : 'lazy'}
      decoding={isFirstSlide ? 'sync' : 'async'}
      fetchpriority={isFirstSlide ? 'high' : 'auto'}
      onError={onError}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/40 to-transparent" />
  </div>
));

OptimizedImage.displayName = 'OptimizedImage';

// Componente principal del Banner
function Banner({ customBanners, autoAdvance = true, showWhatsAppButton = true }) {
  const banners = customBanners || DEFAULT_BANNERS;
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoAdvance);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const pauseTimeoutRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Callback memoizado para avanzar al siguiente slide
  const nextSlide = useCallback(() => {
    setCurrent(prev => (prev + 1) % banners.length);
  }, [banners.length]);

  // Timer principal del carousel
  const clearTimer = useCarouselTimer(
    isPlaying && !isPaused && !isLoading,
    CONFIG.AUTO_ADVANCE_INTERVAL,
    nextSlide
  );

  // Inicialización y carga
  useEffect(() => {
    const loadTimer = setTimeout(() => {
      setIsLoading(false);
    }, CONFIG.LOADING_DELAY);

    return () => clearTimeout(loadTimer);
  }, []);

  // Funciones de control del carousel memoizadas
  const pauseAutoplay = useCallback(() => {
    setIsPaused(true);
    clearTimer();
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, CONFIG.PAUSE_DURATION);
  }, [clearTimer]);

  const handleNext = useCallback(() => {
    nextSlide();
    pauseAutoplay();
  }, [nextSlide, pauseAutoplay]);

  const handlePrev = useCallback(() => {
    setCurrent(prev => (prev === 0 ? banners.length - 1 : prev - 1));
    pauseAutoplay();
  }, [banners.length, pauseAutoplay]);

  const handleDotClick = useCallback((index) => {
    setCurrent(index);
    pauseAutoplay();
  }, [pauseAutoplay]);

  // Manejo de acciones con diferentes tipos
  const handleAction = useCallback((actionType) => {
    const whatsappNumber = `521${CONFIG.WHATSAPP_NUMBER}`;
    
    try {
      switch (actionType) {
        case 'quote':
          window.open(
            `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Buenos días, me interesa conocer los planes de atención personalizada de Geriatría Serena para un familiar en Guadalajara.')}`,
            '_blank',
            'noopener,noreferrer'
          );
          break;
        case 'whatsapp':
          window.open(
            `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, quisiera obtener información sobre los servicios de enfermería geriátrica de élite de Geriatría Serena.')}`,
            '_blank',
            'noopener,noreferrer'
          );
          break;
        case 'call':
        default:
          window.location.href = `tel:${CONFIG.PHONE_NUMBER}`;
          break;
      }
    } catch (error) {
      console.error('Error al ejecutar la acción:', error);
      // Fallback para mostrar información de contacto
      const message = `Para contactarnos:\nTeléfono: ${CONFIG.PHONE_NUMBER}\nWhatsApp: ${CONFIG.WHATSAPP_NUMBER}`;
      alert(message);
    }
  }, []);

  // Manejo de eventos de teclado
  const handleKeyDown = useCallback((event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }, []);

  // Manejo de gestos táctiles
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  }, [touchStart, touchEnd, handleNext, handlePrev]);

  // Control de pausa por hover
  const handleMouseEnter = useCallback(() => {
    if (autoAdvance) {
      setIsPaused(true);
      clearTimer();
    }
  }, [autoAdvance, clearTimer]);

  const handleMouseLeave = useCallback(() => {
    if (autoAdvance) {
      setIsPaused(false);
    }
  }, [autoAdvance]);

  // Manejo de errores de carga de imágenes
  const handleImageError = useCallback((bannerIndex) => {
    setImageErrors(prev => new Set(prev).add(bannerIndex));
    console.error(`Error cargando imagen del banner: ${banners[bannerIndex]?.image}`);
  }, [banners]);

  // Memoización de estilos dinámicos
  const carouselStyle = useMemo(() => ({
    transform: `translateX(-${current * 100}%)`,
    transition: prefersReducedMotion ? 'none' : `transform ${CONFIG.ANIMATION_DURATION}ms ease-in-out`
  }), [current, prefersReducedMotion]);

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      clearTimer();
    };
  }, [clearTimer]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <section 
      className="relative h-[520px] sm:h-[600px] md:h-[680px] overflow-hidden bg-gray-900 group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label="Banner principal de servicios de enfermería"
    >
      {/* Carousel principal */}
      <div 
        className="flex h-full"
        style={carouselStyle}
        role="group"
        aria-live="polite"
        aria-atomic="true"
      >
        {banners.map((banner, index) => (
          <div key={banner.id || index} className="w-full flex-shrink-0 relative">
            {!imageErrors.has(index) && (
              <OptimizedImage
                banner={banner}
                isActive={index === current}
                isFirstSlide={index === 0}
                onError={() => handleImageError(index)}
              />
            )}
            
            {/* Contenido del banner */}
            <div className="relative h-full flex items-center px-4 sm:px-8 lg:px-16 py-8 sm:py-10 z-10">
              <div className={`max-w-2xl text-white animate-fade-in ${banner.highlights ? 'space-y-3 sm:space-y-4' : 'space-y-4'}`}>
                {banner.badge && (
                  <span className="inline-flex items-center gap-1.5 bg-accent-400 text-primary-900 font-bold text-sm sm:text-base px-4 py-1.5 rounded-full shadow-lg">
                    <GiftIcon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    {banner.badge}
                  </span>
                )}
                <h2 className={`font-bold leading-tight drop-shadow-lg ${banner.highlights ? 'text-xl sm:text-2xl md:text-3xl' : 'text-2xl sm:text-3xl md:text-5xl'}`}>
                  {banner.title}
                </h2>
                {banner.subtitle && (
                  <p className="text-base sm:text-lg md:text-xl opacity-90 leading-relaxed drop-shadow-md max-w-xl">
                    {banner.subtitle}
                  </p>
                )}
                {banner.highlights && (
                  <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-4 max-w-xl">
                    {banner.highlights.map((item) => (
                      <div key={item.value} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2.5 sm:py-4">
                        <p className="text-xl sm:text-3xl font-extrabold text-accent-300 drop-shadow">{item.value}</p>
                        <p className="text-xs sm:text-base font-medium mt-0.5">{item.label}</p>
                      </div>
                    ))}
                  </div>
                )}
                {banner.note && (
                  <p className="text-xs sm:text-sm opacity-80 max-w-md">{banner.note}</p>
                )}
                <button
                  onClick={() => handleAction(banner.actionType)}
                  onKeyDown={(e) => handleKeyDown(e, () => handleAction(banner.actionType))}
                  className="mt-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:from-blue-700 focus:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-500/50 px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition-all duration-300 text-sm sm:text-base font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95 backdrop-blur-sm"
                  aria-label={`${banner.buttonText} - ${banner.title}`}
                >
                  <span className="flex items-center gap-2">
                    {banner.buttonText}
                    <span className="text-lg" aria-hidden="true">→</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Indicadores de progreso */}
      <div 
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black/20 backdrop-blur-sm rounded-full px-4 py-2"
        role="tablist"
        aria-label="Seleccionar banner"
      >
        {banners.map((banner, i) => (
          <button
            key={banner.id || i}
            onClick={() => handleDotClick(i)}
            onKeyDown={(e) => handleKeyDown(e, () => handleDotClick(i))}
            className={`h-2 w-8 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
              i === current 
                ? 'bg-white shadow-lg scale-110' 
                : 'bg-white/40 hover:bg-white/60'
            }`}
            role="tab"
            aria-selected={i === current}
            aria-label={`Ir a ${banner.title}`}
          />
        ))}
      </div>

      {/* Controles de navegación */}
      <button
        onClick={handlePrev}
        onKeyDown={(e) => handleKeyDown(e, handlePrev)}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/20 hover:bg-black/30 focus:bg-black/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 backdrop-blur-sm group-hover:opacity-100 opacity-60"
        aria-label="Banner anterior"
      >
        <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </button>
      
      <button
        onClick={handleNext}
        onKeyDown={(e) => handleKeyDown(e, handleNext)}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/20 hover:bg-black/30 focus:bg-black/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 backdrop-blur-sm group-hover:opacity-100 opacity-60"
        aria-label="Siguiente banner"
      >
        <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </button>

      {/* Control de reproducción */}
      {autoAdvance && (
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/30 focus:bg-black/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 backdrop-blur-sm"
          aria-label={isPlaying ? 'Pausar slideshow' : 'Reproducir slideshow'}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>
      )}

      {/* Indicador de estado para lectores de pantalla */}
      <div className="sr-only" aria-live="polite">
        Banner {current + 1} de {banners.length}: {banners[current].title}
      </div>

{/* Botón de WhatsApp y llamada flotante */}
{showWhatsAppButton && (
  <div className="fixed bottom-20 right-6 lg:bottom-6 z-40 flex flex-col gap-3">
    {/* Nuevo botón de llamada */}
    <button
      onClick={() => handleAction('call')}
      className="group/phone transition-all duration-300 hover:scale-110 active:scale-95"
      aria-label="Llamar por teléfono"
    >
      <div className="relative">
        <div className="w-14 h-14 bg-green-600 rounded-full shadow-lg group-hover/phone:shadow-xl transition-shadow duration-300 flex items-center justify-center">
          <PhoneIcon className="w-6 h-6 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full"></div>
      </div>
    </button>
          
    <a 
      href={`https://wa.me/521${CONFIG.WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola, quisiera conocer los servicios de enfermería geriátrica de élite de Geriatría Serena.')}`}
      target="_blank" 
      rel="noopener noreferrer"
      className="transition-all duration-300 hover:scale-110 active:scale-95 animate-pulse hover:animate-none group/whatsapp"
      aria-label="Contactar por WhatsApp"
    >
      <div className="relative">
        <img 
          src={whatsappIcon} 
          alt="WhatsApp" 
          width="56" 
          height="56"
          className="rounded-full shadow-lg group-hover/whatsapp:shadow-xl transition-shadow duration-300"
        />
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full"></div>
      </div>
    </a>
  </div>
)}
    </section>
  );
}

Banner.propTypes = {
  customBanners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      badge: PropTypes.string,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      highlights: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired
        })
      ),
      note: PropTypes.string,
      buttonText: PropTypes.string.isRequired,
      actionType: PropTypes.oneOf(['call', 'whatsapp', 'quote'])
    })
  ),
  autoAdvance: PropTypes.bool,
  showWhatsAppButton: PropTypes.bool
};

export default React.memo(Banner);
